import React, {
  Key,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ComponentDimensions } from '../../types/Layout';

import './index.css';

type ChildHeight = {
  key: Key | null;
  height: number;
};

export interface XAxisInfo {
  axisStyle: any;
  tickValues: number[];
  tickFormat: any;
  domain: { x: [number, number] };
  domainPadding: { x: number };
  chartPadding: {
    top: number;
    left: number;
    bottom: number;
    right: number;
  };
}

interface Props extends PropsWithChildren {
  dimensions?: ComponentDimensions | undefined;
}

const MultiChart = (props: Props) => {
  const { children, dimensions } = props;

  /** Receives Multiple MultiChart items
   * Returns on div that combines them
   */

  const rootRef = useRef<HTMLDivElement | null>(null);
  const dividerRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(dimensions);
  const [childrenHeights, setChildrenHeights] = useState<
    ChildHeight[] | undefined
  >();

  const [cursorPositionX, setCursorPositionX] = useState<number | undefined>(
    undefined,
  );

  const [xAxisInfo, setXAxisInfo] = useState<XAxisInfo | undefined>();

  const [currentDividerPositionY, setCurrentDividerPositionY] = useState<
    | {
        mouseStartPosition: number;
        chartHeightStartHeight: number;
        dividerIndex: number;
      }
    | undefined
  >();

  const updatedChildren = useMemo(() => {
    if (!children || !rootDimensions) {
      return undefined;
    }

    const allChildren: ReactElement[] = [];
    const isArray = Array.isArray(children);

    if (isArray) {
      const filteredChildren = children.filter((c) => !!c);

      if (filteredChildren.length === 1) {
        allChildren.push(filteredChildren[0]);
      } else {
        allChildren.push(...filteredChildren);
      }
    } else {
      allChildren.push(children as ReactElement);
    }

    const clonedChildren = allChildren.map((c, i) =>
      React.cloneElement(c, {
        width: rootDimensions.width,
        height: childrenHeights?.[i]?.height || rootDimensions.height / 5,
        showXAxis: i === allChildren.length - 1,
        // xAxisInfo,
        // setXAxisInfo,
        ...(i === 0 && allChildren.length > 1 && { setXAxisInfo }),
        ...(i !== 0 && { xAxisInfo }),
      }),
    );

    return clonedChildren;
  }, [children, childrenHeights, rootDimensions, xAxisInfo]);

  const fullHeight = useMemo(
    () => childrenHeights?.reduce((acc, curr) => acc + curr.height, 0),
    [childrenHeights],
  );

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  const handleMouseOver = useCallback(
    (e: any) => {
      if (e.nativeEvent.offsetX > 0) {
        setCursorPositionX(e.nativeEvent.offsetX);
      }

      if (!currentDividerPositionY) {
        return;
      }

      /** Negative means drag up, positive means drag down */
      const diff = e.clientY - currentDividerPositionY.mouseStartPosition;

      setChildrenHeights((prev) => {
        if (!prev) {
          return prev;
        }

        const updatedChildrenHeights = [...prev];
        updatedChildrenHeights[currentDividerPositionY.dividerIndex].height =
          currentDividerPositionY.chartHeightStartHeight + diff;

        return updatedChildrenHeights;
      });
    },
    [currentDividerPositionY],
  );

  const handleMouseOut = useCallback(() => {
    setCursorPositionX(undefined);
  }, []);

  useEffect(() => {
    if (
      !!childrenHeights?.length ||
      !rootDimensions ||
      !updatedChildren?.length
    ) {
      return;
    }

    /** Set the initial heights of the children */
    const mainChartHeight = rootDimensions.height;
    const indicatorHeight = Math.round(mainChartHeight / 5);

    const heights: Array<ChildHeight> = [
      { key: updatedChildren[0].key, height: mainChartHeight },
    ];

    const indicatorsAmount = updatedChildren.length - 1;

    for (let i = 0; i < indicatorsAmount; i++) {
      heights.push({
        key: updatedChildren[i + 1].key,
        height: indicatorHeight,
      });
    }

    setChildrenHeights(heights);
  }, [childrenHeights, rootDimensions, updatedChildren]);

  useEffect(() => {
    /** When a child is added, update child heights
     * If the amount of children is lesser than 4,
     * we want to change the size of the main chart
     */
    if (
      !childrenHeights?.length ||
      !rootDimensions ||
      !updatedChildren?.length
    ) {
      return;
    }

    if (updatedChildren.length <= childrenHeights.length) {
      return;
    }

    const change = updatedChildren.length - childrenHeights.length;

    const newHeights: Array<ChildHeight> = [];

    for (let i = 0; i < change; i++) {
      newHeights.push({
        key: updatedChildren[childrenHeights.length + i].key,
        height: Math.round(rootDimensions.height / 5),
      });
    }

    setChildrenHeights((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedChildrenHeights = [...prev, ...newHeights];

      if (updatedChildren.length < 4) {
        /** Update the height of the main chart as well */
        const mainChartHeight = Math.round(
          rootDimensions.height -
            (rootDimensions.height / 5) * (updatedChildrenHeights.length - 1),
        );

        updatedChildrenHeights[0].height = mainChartHeight;
      }

      return updatedChildrenHeights;
    });
  }, [childrenHeights, rootDimensions, updatedChildren]);

  useEffect(() => {
    /** When a child is removed,
     * we need to find out which one is removed
     * and update the childrenHeights */
    if (
      !childrenHeights?.length ||
      !rootDimensions ||
      !updatedChildren?.length
    ) {
      return;
    }

    if (updatedChildren.length >= childrenHeights.length) {
      return;
    }

    const missingChildIndex = childrenHeights.findIndex(
      ({ key }) =>
        updatedChildren.findIndex((child) => child.key === key) === -1,
    );

    if (missingChildIndex === -1) {
      return;
    }

    setChildrenHeights((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedChildrenHeights = [...prev];

      updatedChildrenHeights.splice(missingChildIndex, 1);

      if (updatedChildren.length < 4) {
        /** Update the height of the main chart as well */
        const mainChartHeight = Math.round(
          rootDimensions.height -
            (rootDimensions.height / 5) * (updatedChildrenHeights.length - 1),
        );

        updatedChildrenHeights[0].height = mainChartHeight;
      }

      return updatedChildrenHeights;
    });
  }, [childrenHeights, rootDimensions, updatedChildren]);

  const handleDividerRelease = useCallback(() => {
    if (!rootRef.current) {
      return;
    }

    setCurrentDividerPositionY(undefined);
  }, []);

  const handleDividerGrab = useCallback(
    (event: any, dividerIndex: number) => {
      if (!rootRef.current) {
        return;
      }

      setCurrentDividerPositionY({
        mouseStartPosition: event.clientY,
        chartHeightStartHeight: childrenHeights?.[dividerIndex].height ?? 0,
        dividerIndex: dividerIndex,
      });
    },
    [childrenHeights],
  );

  return (
    <div
      ref={rootRef}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onMouseUp={handleDividerRelease}
      className="multichart-root"
      style={{ width: rootDimensions?.width }}
    >
      {updatedChildren?.map((child, dividerIndex) => {
        return (
          <>
            {child}
            <div className="multichart-divider-container">
              <div
                ref={(ref) => (dividerRefs.current[dividerIndex] = ref)}
                className={`multichart-divider |${child.key}`}
                onMouseDown={(event) => handleDividerGrab(event, dividerIndex)}
                onMouseUp={handleDividerRelease}
              />
            </div>
          </>
        );
      })}
      {/* <div
        className="multichart-cursor-line"
        style={{
          left: cursorPositionX,
          opacity: !!cursorPositionX ? 1 : 0,
          height: fullHeight ? fullHeight - 30 : 0,
        }}
      /> */}
    </div>
  );
};

export default MultiChart;
